import React from 'react';
import { API_URL } from '../../config/apiConfig';
import safeStorage from '../../utils/safeStorage';

/**
 * Global Error Boundary
 * 
 * Catches unhandled React render errors anywhere in the component tree.
 * Shows a friendly fallback UI and POSTs the error to /api/errors/log
 * so it appears in the admin Error Log screen.
 *
 * Wrap this around <App /> in index.js or at the top of App.js routing.
 *
 * Class component required — React error boundaries must use
 * componentDidCatch / getDerivedStateFromError (no hook equivalent).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      reported: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    this.reportError(error, errorInfo);
  }

  async reportError(error, errorInfo) {
    try {
      // Try to get user_id from localStorage token (best-effort)
      let userId = null;
      try {
        const token = safeStorage.getItem('authToken') || safeStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id || payload.userId;
        }
      } catch (_) { /* ignore */ }

      await fetch(`${API_URL}/errors/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          severity: 'error',
          source: 'frontend',
          message: error?.message || String(error),
          stack_trace: error?.stack,
          error_code: error?.name || 'ReactError',
          component_stack: errorInfo?.componentStack,
          page_url: window.location.href,
          user_id: userId,
          metadata: {
            userAgent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString()
          }
        })
      });

      this.setState({ reported: true });
    } catch (reportErr) {
      // If error reporting itself fails, just log to console
      console.error('Failed to report error to server:', reportErr);
    }
  }

  handleReload = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, reported: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            maxWidth: '500px',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            textAlign: 'center',
            border: '1px solid #e9ecef'
          }}>
            {/* Warning icon */}
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>

            <h2 style={{
              color: '#333',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '12px'
            }}>
              Something went wrong
            </h2>

            <p style={{
              color: '#555',
              fontSize: '0.9rem',
              lineHeight: '1.5',
              marginBottom: '24px'
            }}>
              An unexpected error occurred. The error has been reported to our team
              and we'll look into it.
            </p>

            {/* Show error message in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div style={{
                backgroundColor: '#fff5f5',
                border: '1px solid #fed7d7',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <p style={{ color: '#c53030', fontSize: '0.8rem', fontWeight: '600', marginBottom: '6px' }}>
                  {this.state.error.name}: {this.state.error.message}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre style={{
                    color: '#742a2a',
                    fontSize: '0.7rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '200px',
                    overflow: 'auto',
                    margin: 0
                  }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleRetry}
                style={{
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={e => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={e => e.target.style.backgroundColor = '#2563eb'}
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  backgroundColor: 'white',
                  color: '#333',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => { e.target.style.backgroundColor = '#f8f9fa'; e.target.style.borderColor = '#dee2e6'; }}
                onMouseLeave={e => { e.target.style.backgroundColor = 'white'; e.target.style.borderColor = '#e9ecef'; }}
              >
                Go to Home
              </button>
            </div>

            {this.state.reported && (
              <p style={{ color: '#888', fontSize: '0.75rem', marginTop: '16px' }}>
                ✓ Error report submitted
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
